import React, {
  useState,
  useMemo,
  useCallback,
  useEffect,
  useRef,
} from 'react';
import PropTypes from 'prop-types';
import { FixedSizeList } from 'react-window';
import styledComponent from '../../utils/styledComponent';
import theme from '../../theme';
import Loader from './Loader';

const DEFAULT_ROW_HEIGHT = 48;
const DEFAULT_LIST_HEIGHT = 600;
const DEFAULT_COLUMN_WIDTH = 120;
const MIN_COLUMN_WIDTH = 60;

const StyledDiv = styledComponent('div', {
  width: '100%',
  minWidth: 0,
  '& .dt-wrapper': {
    background: theme.colors.bgCard,
    borderRadius: theme.radii.card,
    border: theme.borders.card,
    boxShadow: theme.shadows.card,
    overflowX: 'auto',
    overflowY: 'hidden',
    width: '100%',
    maxWidth: '100%',
  },
  '& .dt-table': {
    ...theme.typography.body,
  },
  '& .dt-header-row': {
    background: theme.colors.bgMuted,
    borderBottom: theme.borders.card,
    fontWeight: theme.fontWeights.semibold,
    color: theme.colors.text,
    userSelect: 'none',
  },
  '& .dt-header-cell': {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing.cellPadding,
    minWidth: 0,
  },
  '& .dt-header-label': {
    flex: 1,
    minWidth: 0,
    textAlign: 'left',
    background: 'transparent',
    border: 'none',
    color: 'inherit',
    font: 'inherit',
    fontWeight: 'inherit',
    padding: 0,
    cursor: 'default',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  '& .dt-header-label.sortable': {
    cursor: 'pointer',
  },
  '& .dt-header-label.sortable:hover': {
    color: theme.colors.primary,
  },
  '& .dt-header-label.sortable:focus': {
    outline: 'none',
    boxShadow: `inset 0 0 0 2px ${theme.colors.focusRing}`,
    color: theme.colors.primary,
  },
  '& .dt-resize-handle': {
    position: 'absolute',
    top: 0,
    right: 0,
    bottom: 0,
    width: '6px',
    cursor: 'col-resize',
    userSelect: 'none',
    touchAction: 'none',
  },
  '& .dt-resize-handle:hover, & .dt-resize-handle.dragging': {
    background: theme.colors.focusRing,
    opacity: 0.4,
  },
  '& .dt-body-row': {
    borderBottom: theme.borders.card,
    boxSizing: 'border-box',
  },
  '& .dt-body-row:last-child': {
    borderBottom: 'none',
  },
  '& .dt-cell': {
    padding: theme.spacing.cellPadding,
    color: theme.colors.textBody,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    minWidth: 0,
    display: 'flex',
    alignItems: 'center',
  },
  '& .dt-empty': {
    textAlign: 'center',
    padding: theme.spacing.emptyStatePadding,
    color: theme.colors.textMuted,
    fontStyle: 'italic',
  },
  '& .dt-sort-arrow': {
    marginLeft: theme.spacing.gapSm,
    color: theme.colors.textHint,
  },
});

function compareValues(a, b) {
  if (a == null && b == null) return 0;
  if (a == null) return -1;
  if (b == null) return 1;
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  return String(a).localeCompare(String(b));
}

function buildInitialWidths(columns) {
  return columns.reduce((acc, col) => {
    acc[col.key] = col.width || DEFAULT_COLUMN_WIDTH;
    return acc;
  }, {});
}

function DataTable({
  columns,
  rows,
  rowKey,
  emptyMessage,
  isLoading,
  rowHeight,
  maxHeight,
}) {
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState('asc');
  const [widths, setWidths] = useState(() => buildInitialWidths(columns));
  const [draggingKey, setDraggingKey] = useState(null);

  // Reset widths only when the set of column keys changes (not on every render).
  const columnKeysSignature = columns.map((c) => c.key).join('|');
  useEffect(() => {
    setWidths(buildInitialWidths(columns));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columnKeysSignature]);

  const sortedRows = useMemo(() => {
    if (!sortKey) return rows;
    const copy = [...rows];
    copy.sort((a, b) => {
      const cmp = compareValues(a[sortKey], b[sortKey]);
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return copy;
  }, [rows, sortKey, sortDir]);

  const handleHeaderClick = useCallback(
    (col) => {
      if (!col.sortable) return;
      if (sortKey === col.key) {
        setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
      } else {
        setSortKey(col.key);
        setSortDir('asc');
      }
    },
    [sortKey, sortDir],
  );

  const totalWidth = useMemo(
    () =>
      columns.reduce(
        (sum, col) => sum + (widths[col.key] || DEFAULT_COLUMN_WIDTH),
        0,
      ),
    [columns, widths],
  );

  const gridStyle = useMemo(
    () => ({
      display: 'grid',
      gridTemplateColumns: columns
        .map((col) => `${widths[col.key] || DEFAULT_COLUMN_WIDTH}px`)
        .join(' '),
      width: totalWidth,
    }),
    [columns, widths, totalWidth],
  );

  const dragStateRef = useRef(null);

  const handleResizeStart = useCallback(
    (e, colKey) => {
      e.preventDefault();
      e.stopPropagation();
      const startX = e.clientX;
      const startWidth = widths[colKey] || DEFAULT_COLUMN_WIDTH;
      dragStateRef.current = { colKey, startX, startWidth };
      setDraggingKey(colKey);

      const onMove = (moveEvent) => {
        const state = dragStateRef.current;
        if (!state) return;
        const delta = moveEvent.clientX - state.startX;
        const newWidth = Math.max(MIN_COLUMN_WIDTH, state.startWidth + delta);
        setWidths((prev) => ({ ...prev, [state.colKey]: newWidth }));
      };

      const onUp = () => {
        dragStateRef.current = null;
        setDraggingKey(null);
        document.removeEventListener('mousemove', onMove);
        document.removeEventListener('mouseup', onUp);
      };

      document.addEventListener('mousemove', onMove);
      document.addEventListener('mouseup', onUp);
    },
    [widths],
  );

  const renderArrow = (col) => {
    if (!col.sortable || sortKey !== col.key) return null;
    return (
      <span className="dt-sort-arrow">{sortDir === 'asc' ? '▲' : '▼'}</span>
    );
  };

  if (isLoading) {
    return (
      <StyledDiv>
        <div className="dt-wrapper">
          <Loader />
        </div>
      </StyledDiv>
    );
  }

  const listHeight = Math.min(sortedRows.length * rowHeight, maxHeight);

  const Row = ({ index, style }) => {
    const row = sortedRows[index];
    return (
      <div
        role="row"
        className="dt-body-row"
        style={{ ...style, ...gridStyle }}
      >
        {columns.map((col) => (
          <div role="cell" className="dt-cell" key={col.key}>
            {col.render ? col.render(row) : row[col.key]}
          </div>
        ))}
      </div>
    );
  };

  Row.propTypes = {
    index: PropTypes.number.isRequired,
    style: PropTypes.object.isRequired,
  };

  return (
    <StyledDiv>
      <div className="dt-wrapper">
        <div role="table" className="dt-table" style={{ width: totalWidth }}>
          <div role="rowgroup">
            <div role="row" className="dt-header-row" style={gridStyle}>
              {columns.map((col) => {
                const isSorted = col.sortable && sortKey === col.key;
                let ariaSort;
                if (isSorted) {
                  ariaSort = sortDir === 'asc' ? 'ascending' : 'descending';
                }
                const labelClass = col.sortable
                  ? 'dt-header-label sortable'
                  : 'dt-header-label';
                const handleClass =
                  draggingKey === col.key
                    ? 'dt-resize-handle dragging'
                    : 'dt-resize-handle';
                return (
                  <div
                    key={col.key}
                    role="columnheader"
                    aria-sort={ariaSort}
                    className="dt-header-cell"
                  >
                    {col.sortable ? (
                      <button
                        type="button"
                        className={labelClass}
                        onClick={() => handleHeaderClick(col)}
                      >
                        {col.label}
                        {renderArrow(col)}
                      </button>
                    ) : (
                      <div className={labelClass}>{col.label}</div>
                    )}
                    <div
                      className={handleClass}
                      onMouseDown={(e) => handleResizeStart(e, col.key)}
                      role="separator"
                      aria-orientation="vertical"
                      aria-label={`Resize ${col.label} column`}
                    />
                  </div>
                );
              })}
            </div>
          </div>
          {sortedRows.length === 0 ? (
            <div className="dt-empty">{emptyMessage}</div>
          ) : (
            <div role="rowgroup">
              <FixedSizeList
                height={listHeight}
                itemCount={sortedRows.length}
                itemSize={rowHeight}
                width={totalWidth}
                itemKey={(index) => sortedRows[index][rowKey]}
                style={{ overflowX: 'hidden' }}
              >
                {Row}
              </FixedSizeList>
            </div>
          )}
        </div>
      </div>
    </StyledDiv>
  );
}

DataTable.propTypes = {
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      sortable: PropTypes.bool,
      render: PropTypes.func,
      width: PropTypes.number,
    }),
  ).isRequired,
  rows: PropTypes.arrayOf(PropTypes.object).isRequired,
  rowKey: PropTypes.string.isRequired,
  emptyMessage: PropTypes.string,
  isLoading: PropTypes.bool,
  rowHeight: PropTypes.number,
  maxHeight: PropTypes.number,
};

DataTable.defaultProps = {
  emptyMessage: 'No data.',
  isLoading: false,
  rowHeight: DEFAULT_ROW_HEIGHT,
  maxHeight: DEFAULT_LIST_HEIGHT,
};

export default DataTable;
